/**
 * Annotation formatting utilities for in-context pointing.
 */

import { Annotations } from "../apiContracts/annotation";

/**
 * Groups annotations by their label. Within each label group, points appear first
 * followed by bounding boxes, preserving the original order within each annotation type.
 */
function groupAnnotationsByLabel(annotations: Annotations): Map<string, string[]> {
  const grouped = new Map<string, string[]>();

  // Process points first
  for (const point of annotations.points) {
    const formatted = `<point> (${point.x},${point.y}) </point>`;
    const existing = grouped.get(point.label) || [];
    existing.push(formatted);
    grouped.set(point.label, existing);
  }

  // Process bounding boxes second
  for (const bbox of annotations.boundingBoxes) {
    const x2 = bbox.x + bbox.width;
    const y2 = bbox.y + bbox.height;
    const formatted = `<point_box> (${bbox.x},${bbox.y}) (${x2},${y2}) </point_box>`;
    const existing = grouped.get(bbox.label) || [];
    existing.push(formatted);
    grouped.set(bbox.label, existing);
  }

  return grouped;
}

/**
 * Format annotations into structured text representation.
 * Returns XML-like format: <collection mention="label"> <point> (x,y) </point> </collection>
 */
export function formatAnnotations(annotations: Annotations): string {
  const grouped = groupAnnotationsByLabel(annotations);

  // Build collection format
  const collections: string[] = [];
  for (const [label, annotationParts] of grouped) {
    const innerContent = annotationParts.join(" ");
    const collection = `<collection mention="${label}"> ${innerContent} </collection>`;
    collections.push(collection);
  }

  return collections.join(" ");
}
